// Pledge (cláusula pignoraticia) and exclusive (carácter excluyente) designations
// in the contracting funnel: the two modalities of «Modificar beneficiario» the
// funnel did not offer. Same body and same limits as the post-sale request
// (BeneficiariesChangePayload in the backend), so a designation made here and one
// made later from the dashboard are the same thing.

export type ClauseModalityT = "PLEDGE_CLAUSE" | "EXCLUSIVE";
export type SubsidiaryModeT = "EQUAL_PARTS" | "ORDERED";

export interface ClausePersonI {
  name: string;
  lastName: string;
  lastName2: string | null;
  idNumber: string;
}

export interface ClauseBankI {
  bankName: string;
  loanNumber: string;
}

export interface PledgeDesignationI {
  modality: "PLEDGE_CLAUSE";
  banks: ClauseBankI[];
  remainderBeneficiaries: ClausePersonI[];
}

export interface ExclusiveDesignationI {
  modality: "EXCLUSIVE";
  principal: ClausePersonI;
  subsidiaryMode: SubsidiaryModeT;
  subsidiaries: ClausePersonI[];
}

export type BeneficiaryDesignationT = PledgeDesignationI | ExclusiveDesignationI;

export interface BeneficiaryDesignationViewI {
  available: boolean;
  designation: BeneficiaryDesignationT | null;
}

export interface PersonRowI {
  uid: number;
  name: string;
  lastName: string;
  lastName2: string;
  idNumber: string;
}

export interface BankRowI {
  uid: number;
  bankName: string;
  loanNumber: string;
}

export const CLAUSE_LIMITS = {
  banks: 4,
  remainder: 6,
  subsidiaries: 6,
};

const ID_MAX_LENGTH = 50;

const ROUTE_PARAM: Record<ClauseModalityT, string> = {
  PLEDGE_CLAUSE: "pledge",
  EXCLUSIVE: "exclusive",
};

export const isClauseModality = (value: unknown): value is ClauseModalityT =>
  value === "PLEDGE_CLAUSE" || value === "EXCLUSIVE";

export const clauseRoute = (modality: ClauseModalityT): string =>
  `/beneficiaries/designation/${ROUTE_PARAM[modality]}`;

export const modalityFromRouteParam = (
  param: string | undefined
): ClauseModalityT | null => {
  if (param === ROUTE_PARAM.PLEDGE_CLAUSE) return "PLEDGE_CLAUSE";
  if (param === ROUTE_PARAM.EXCLUSIVE) return "EXCLUSIVE";
  return null;
};

// Stable identity per row: the inputs are keyed by it, so removing a row in the
// middle never shows the next row's values in the removed one's inputs.
let lastUid = 0;
const nextUid = (): number => {
  lastUid += 1;
  return lastUid;
};

export const emptyPerson = (): PersonRowI => ({
  uid: nextUid(),
  name: "",
  lastName: "",
  lastName2: "",
  idNumber: "",
});

export const emptyBank = (): BankRowI => ({
  uid: nextUid(),
  bankName: "",
  loanNumber: "",
});

export const personRows = (people: ClausePersonI[]): PersonRowI[] =>
  people.map((p) => ({
    uid: nextUid(),
    name: p.name,
    lastName: p.lastName,
    lastName2: p.lastName2 ?? "",
    idNumber: p.idNumber,
  }));

export const bankRows = (banks: ClauseBankI[]): BankRowI[] =>
  banks.map((b) => ({ uid: nextUid(), bankName: b.bankName, loanNumber: b.loanNumber }));

export const personRowValid = (p: PersonRowI): boolean =>
  p.name.trim() !== "" &&
  p.lastName.trim() !== "" &&
  p.idNumber.trim() !== "" &&
  p.idNumber.trim().length <= ID_MAX_LENGTH;

export const bankRowValid = (b: BankRowI): boolean =>
  b.bankName.trim() !== "" && b.loanNumber.trim() !== "";

const withinLimit = (count: number, max: number): boolean => count >= 1 && count <= max;

export const pledgeValid = (banks: BankRowI[], remainder: PersonRowI[]): boolean =>
  withinLimit(banks.length, CLAUSE_LIMITS.banks) &&
  withinLimit(remainder.length, CLAUSE_LIMITS.remainder) &&
  banks.every(bankRowValid) &&
  remainder.every(personRowValid);

export const exclusiveValid = (principal: PersonRowI, subsidiaries: PersonRowI[]): boolean =>
  personRowValid(principal) &&
  withinLimit(subsidiaries.length, CLAUSE_LIMITS.subsidiaries) &&
  subsidiaries.every(personRowValid);

const personPayload = (p: PersonRowI): ClausePersonI => ({
  name: p.name.trim(),
  lastName: p.lastName.trim(),
  lastName2: p.lastName2.trim() !== "" ? p.lastName2.trim() : null,
  idNumber: p.idNumber.trim(),
});

export const pledgePayload = (
  banks: BankRowI[],
  remainder: PersonRowI[]
): PledgeDesignationI => ({
  modality: "PLEDGE_CLAUSE",
  banks: banks.map((b) => ({ bankName: b.bankName.trim(), loanNumber: b.loanNumber.trim() })),
  remainderBeneficiaries: remainder.map(personPayload),
});

export const exclusivePayload = (
  principal: PersonRowI,
  subsidiaryMode: SubsidiaryModeT,
  subsidiaries: PersonRowI[]
): ExclusiveDesignationI => ({
  modality: "EXCLUSIVE",
  principal: personPayload(principal),
  subsidiaryMode,
  subsidiaries: subsidiaries.map(personPayload),
});
